import {CastorService} from '@aipb/gulliver/castor/data-access';
import {CastorConfigService, SearchService} from '@aipb/gulliver/catalog/data-access';
import {CatalogEnvService} from '@aipb/gulliver/catalog/env';
import {CatalogRouting} from '@aipb/gulliver/catalog/routing';
import {ROUTE_CATEGORY_COMPARE, ROUTE_CATEGORY_DETAIL, ROUTE_CATEGORY_TOPIC} from '@aipb/gulliver/catalog/util';
import {FrameRepository} from '@aipb/gulliver/frame/data-access';
import {RouteItem} from '@aipb/gulliver/frame/model';
import {ApplicationInitGuard} from '@aipb/gulliver/util-app';
import {inject, Injectable} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';

@Injectable()
export class CatalogInitGuard extends ApplicationInitGuard {
  private catalogRouting = inject(CatalogRouting);
  private castor = inject(CastorService);
  private transloco = inject(TranslocoService);
  private castorConfigService = inject(CastorConfigService);
  private searchService = inject(SearchService);
  private frameRepository = inject(FrameRepository);

  constructor(protected catalogEnv: CatalogEnvService) {
    super('catalog', catalogEnv);
  }

  setup() {
    this.castor.setCastorUrl(this.catalogEnv.env.castor.url);
    this.transloco.setTranslation(
      {
        catalog: {
          titles: this.catalogEnv.env[this.transloco.getActiveLang()]
        }
      },
      this.transloco.getActiveLang(),
      {
        merge: true
      }
    );

    this.castor.ping().subscribe((data) => {
      // eslint-disable-next-line no-console,no-restricted-syntax
      console.info(
        `%c❯ 🔍️ Catalog ${data.server.system}:%c v${data.server.version} (${data.server.build})`,
        'font-size: 14px; font-weight: bold',
        ''
      );
    });

    this.castorConfigService.load(true);

    const isMobile = this.frameRepository.isMobile;

    if (isMobile) {
      this.searchService.hideFacetList();
    }

    this.frame.setStaticRoutes(this.getStaticRoutes());

    this.frame.setRouteCategories([
      {
        id: ROUTE_CATEGORY_TOPIC,
        label: 'catalog.titles.topics',
        icon: 'folder'
      },
      {
        id: ROUTE_CATEGORY_DETAIL,
        label: 'catalog.titles.pinned_details',
        icon: 'keep'
      },
      {
        id: ROUTE_CATEGORY_COMPARE,
        label: 'catalog.titles.comparison',
        icon: 'text_compare'
      }
    ]);

    this.initTopics();
  }

  private initTopics(): void {
    for (const topic of this.catalogEnv.env.topics || []) {
      this.frame.addDynamicRoute({
        deletable: false,
        category: ROUTE_CATEGORY_TOPIC,
        url: this.catalogRouting.topicSingleRouteUrl(topic.id),
        label: topic.shortLabel
      });
    }
  }

  private getStaticRoutes(): RouteItem[] {
    return [
      ...(!this.catalogEnv.hasMapSearch()
        ? [
          {
            url: this.catalogRouting.searchDefaultRouteUrl(),
            label: 'catalog.routes.search_default',
            deletable: false,
            htmlId: 'menutryit',
            icon: 'search',
            classes: ['flex', 'lg:hidden']
          }
        ]
        : []),
      ...(this.catalogEnv.hasMapSearch()
        ? [
          {
            icon: 'map',
            url: this.catalogRouting.searchMapRouteUrl(),
            label: 'catalog.routes.search_map_default',
            deletable: false,
            htmlId: 'menusearchmap'
          }
        ]
        : []),
      {
        url: this.catalogRouting.searchResultRouteUrl(),
        label: 'catalog.routes.search_result_desktop',
        deletable: false,
        htmlId: 'menusearchrec',
        icon: 'search',
        classes: ['hidden', 'lg:flex']
      },
      {
        url: this.catalogRouting.searchResultRouteUrl(),
        label: 'catalog.routes.search_result_mobile',
        deletable: false,
        htmlId: 'menusearchrec',
        icon: 'list',
        classes: ['flex', 'lg:hidden']
      },
      {
        url: this.catalogRouting.detailDefaultRouteUrl(),
        label: 'catalog.routes.detail_default',
        deletable: false,
        htmlId: 'menudetailrec',
        icon: 'summarize'
      },
      ...(this.catalogEnv.env.news && this.catalogEnv.env.news.length > 0
        ? [
          {
            url: this.catalogRouting.newsDefaultRouteUrl(),
            label: 'catalog.routes.news_default',
            deletable: false,
            htmlId: 'menunews',
            icon: 'campaign'
          }] : []),
      {
        url: this.catalogRouting.settingsDefaultRouteUrl(),
        label: 'catalog.routes.settings_default',
        deletable: false,
        htmlId: 'menusettings',
        icon: 'settings'
      }
    ];
  }
}
